import React, { memo, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBannerList } from "../../store/slices/user";
import styles from "./Slider.module.css";

const Slider = memo(() => {
  const dispatch = useDispatch();
  const { bannerList } = useSelector((state) => state.user);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch banner data from backend
  useEffect(() => {
    dispatch(getBannerList());
  }, [dispatch]);

  // Process banner data into slides format
  useEffect(() => {
    if (bannerList && bannerList.length > 0) {
      const activeBanners = bannerList.filter(
        (banner) => banner.status === "NORMAL"
      );
      const processedSlides = activeBanners
        .map((banner, index) => {
          const imagePath = isMobile
            ? banner.imageUrlMobile || banner.imageUrl || banner.imageUrlPc
            : banner.imageUrlPc || banner.imageUrl || banner.imageUrlMobile;

          if (!imagePath) {
            return null;
          }

          return {
            id: banner.id || index + 1,
            src: `${process.env.REACT_APP_API_BASE_URL}${imagePath}`,
            alt: banner.title || `Banner ${index + 1}`,
            title: banner.title,
          };
        })
        .filter(Boolean);
      setSlides(processedSlides);
    }
  }, [bannerList, isMobile]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  // Auto-play functionality
  useEffect(() => {
    if (slides.length > 0) {
      const interval = setInterval(nextSlide, 5000); // Change slide every 5 seconds
      return () => clearInterval(interval);
    }
  }, [nextSlide, slides.length]);

  // Reset currentSlide when slides change
  useEffect(() => {
    if (slides.length > 0 && currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  // Don't render if no slides
  if (!slides || slides.length === 0) {
    return null;
  }

  return (
    <div className={styles.slider}>
      <div className={styles.sliderContainer}>
        <div
          className={styles.sliderWrapper}
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide) => (
            <div key={slide.id} className={styles.slide}>
              <img
                src={slide.src}
                alt={slide.alt}
                className={styles.slideImage}
                style={{
                  objectFit: "contain",
                  width: "100%",
                  height: "auto",
                }}
              />
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          className={`${styles.navButton} ${styles.prevButton}`}
          onClick={prevSlide}
          aria-label="Previous slide"
          style={{
            position: "absolute",
            top: "50%",
            left: "10px",
            transform: "translateY(-50%)",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            fontSize: "18px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            transition: "all 0.3s ease",
          }}
        >
          ‹
        </button>
        <button
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={nextSlide}
          aria-label="Next slide"
          style={{
            position: "absolute",
            top: "50%",
            right: "10px",
            transform: "translateY(-50%)",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "none",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "white",
            fontSize: "18px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            transition: "all 0.3s ease",
          }}
        >
          ›
        </button>

        {/* Dots Indicator */}
        <div className={styles.dotsContainer}>
          {slides.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${
                index === currentSlide ? styles.activeDot : ""
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

Slider.displayName = "Slider";

export default Slider;
